/**
 * /account/finance-quotes — customer's saved financing quotes.
 *
 * Actions per row:
 *   • Get pre-qualified → convert-to-lead → BDC follow-up
 *   • (No delete — quotes are analytics-worthy; we just don't show unsaved ones.)
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Banknote, Car } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";

const fmtUSD = (cents) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format((cents || 0) / 100);
const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "";

const FinanceQuotes = () => {
  const { user } = useAuth();
  const [quotes, setQuotes] = useState(null);
  const [prequalQuote, setPrequalQuote] = useState(null);
  const [msg, setMsg] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = () => api.get("/me/finance-quotes").then((r) => setQuotes(r.data.items || [])).catch(() => setQuotes([]));
  useEffect(() => { load(); }, []);

  const submitPrequal = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/finance/quote/convert-to-lead", {
        quote_id: prequalQuote.id,
        name: user.full_name, email: user.email, phone: user.phone || "",
        message: msg,
      });
      toast.success("A BeloMotors specialist will follow up shortly.");
      setPrequalQuote(null);
      setMsg("");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="finance-quotes-page">
      <div className="flex items-end justify-between">
        <div>
          <p className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Your saved</p>
          <h1 className="mt-2 font-display text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900">
            Financing quotes
          </h1>
        </div>
        <Button asChild variant="outline" className="border-brand text-brand hover:bg-brand hover:text-white">
          <Link to="/account">← Account</Link>
        </Button>
      </div>

      {quotes === null ? (
        <div className="mt-10 space-y-4">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="h-32 bg-slate-100 rounded-2xl animate-pulse" />
          ))}
        </div>
      ) : quotes.length === 0 ? (
        <div className="mt-16 text-center bg-slate-50 border border-slate-200 rounded-2xl py-16" data-testid="finance-quotes-empty">
          <Banknote size={30} className="mx-auto text-slate-300" />
          <p className="mt-4 font-display text-xl text-slate-800">No saved quotes yet</p>
          <p className="mt-2 text-sm text-slate-500">
            Open any vehicle and use the "Estimate your monthly payment" panel to save a quote.
          </p>
          <Button asChild className="mt-6 bg-brand hover:bg-brand-hover text-white">
            <Link to="/inventory">Browse inventory</Link>
          </Button>
        </div>
      ) : (
        <div className="mt-8 space-y-4" data-testid="finance-quotes-list">
          {quotes.map((q) => {
            const v = q.vehicle;
            const apr = (q.apr_bps / 100).toFixed(2);
            return (
              <div key={q.id} className="rounded-2xl border-2 border-emerald-500/40 bg-white p-5 flex flex-col md:flex-row md:items-center gap-4" data-testid={`finance-quote-row-${q.id}`}>
                {v?.images?.[0] && (
                  <img src={v.images[0]} alt="" className="w-full md:w-32 h-24 md:h-20 object-cover rounded-lg" />
                )}
                <div className="flex-1 min-w-0">
                  <p className="font-display text-lg font-semibold text-slate-900">
                    {v ? `${v.year} ${v.make} ${v.model}` : "Vehicle removed"}
                    {v?.trim ? <span className="text-slate-500 font-normal text-sm"> · {v.trim}</span> : null}
                  </p>
                  <p className="text-xs text-slate-500 mt-0.5">Saved {fmtDate(q.created_at)}</p>
                  <div className="grid grid-cols-4 gap-3 mt-2 text-sm">
                    <div>
                      <p className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Monthly</p>
                      <p className="font-display font-bold text-slate-900 text-lg leading-tight">
                        {fmtUSD(q.monthly_payment_cents)}<span className="text-xs text-slate-400">/mo</span>
                      </p>
                    </div>
                    <div>
                      <p className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Term</p>
                      <p className="font-semibold">{q.loan_term_months} mo</p>
                    </div>
                    <div>
                      <p className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">APR</p>
                      <p className="font-semibold">{apr}%</p>
                    </div>
                    <div>
                      <p className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Down</p>
                      <p className="font-semibold">{fmtUSD(q.down_payment_cents)}</p>
                    </div>
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row md:flex-col gap-2 md:min-w-[180px]">
                  {v?.status === "available" && (
                    <Button asChild variant="outline" className="border-slate-300">
                      <Link to={`/vehicle/${v.id}`} data-testid={`finance-quote-view-vehicle-${q.id}`}>
                        <Car size={12} /> View vehicle
                      </Link>
                    </Button>
                  )}
                  <Button
                    className="bg-emerald-600 hover:bg-emerald-500 text-white font-semibold"
                    onClick={() => setPrequalQuote(q)}
                    disabled={!!q.created_lead_id}
                    data-testid={`finance-quote-prequal-btn-${q.id}`}
                  >
                    {q.created_lead_id ? "Followed up ✓" : "Get pre-qualified"}
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={!!prequalQuote} onOpenChange={(o) => !o && setPrequalQuote(null)}>
        <DialogContent className="max-w-md" data-testid="finance-quotes-prequal-dialog">
          <DialogHeader>
            <DialogTitle>Get pre-qualified</DialogTitle>
            <DialogDescription>
              A BeloMotors financing specialist will reach out about this quote.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submitPrequal} className="space-y-3">
            {prequalQuote && (
              <div className="text-xs bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 text-emerald-900">
                <strong>{fmtUSD(prequalQuote.monthly_payment_cents)}/mo</strong> · {prequalQuote.loan_term_months}mo · APR {(prequalQuote.apr_bps/100).toFixed(2)}%
              </div>
            )}
            <div>
              <Label>Notes (optional)</Label>
              <Input value={msg} onChange={(e) => setMsg(e.target.value)} placeholder="Anything you want the team to know…" />
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setPrequalQuote(null)}>Cancel</Button>
              <Button type="submit" className="bg-emerald-600 hover:bg-emerald-500 text-white" disabled={submitting} data-testid="finance-quotes-prequal-submit-btn">
                {submitting ? "Submitting…" : "Confirm"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FinanceQuotes;
